import { useCallback, useContext, useEffect, useState } from 'react'
import {
    ArrowDownTrayIcon,
    CalendarDaysIcon,
    DocumentChartBarIcon,
    PlusIcon,
    ClockIcon,
} from '@heroicons/react/24/outline'
import { AuthContextApi } from '../../../../Context/api'
import { normalizeRelatorioStatus, relatorioStatusStyle } from '../../../../utils/relatorioStatus'

const formatDateTime = (value) => {
    if (!value) return '—'
    const d = new Date(String(value).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const RelatorioObraTab = ({ obraId, obraCodigo, obraNome, investidor = false }) => {
    const {
        fetchRelatoriosObraByObra,
        fetchRelatoriosObraInvestidor,
        downloadRelatorioObra,
        postRelatorio,
        getDataExibicao,
    } = useContext(AuthContextApi)

    const [relatorios, setRelatorios] = useState([])
    const [loading, setLoading] = useState(true)
    const [downloadingId, setDownloadingId] = useState(null)
    const [solicitando, setSolicitando] = useState(false)

    const [datasExibicao, setDatasExibicao] = useState([])
    const [loadingDatas, setLoadingDatas] = useState(false)
    const [dataExibicaoId, setDataExibicaoId] = useState('')

    const loadRelatorios = useCallback(async () => {
        if (!obraId) {
            setRelatorios([])
            setLoading(false)
            return
        }
        setLoading(true)
        const data = investidor
            ? await fetchRelatoriosObraInvestidor(obraId)
            : await fetchRelatoriosObraByObra({ codigo: obraCodigo, nome: obraNome })
        setRelatorios(Array.isArray(data) ? data : [])
        setLoading(false)
    }, [obraId, obraCodigo, obraNome, investidor, fetchRelatoriosObraByObra, fetchRelatoriosObraInvestidor])

    const loadDatas = useCallback(async () => {
        if (investidor) return
        setLoadingDatas(true)
        const datas = await getDataExibicao({ page: 0, per_page: 100, sort: 'data_inicial', sort_dir: 'desc' })
        setDatasExibicao(Array.isArray(datas) ? datas : [])
        setLoadingDatas(false)
    }, [getDataExibicao, investidor])

    useEffect(() => {
        loadRelatorios()
        loadDatas()
    }, [loadRelatorios, loadDatas])

    const handleDownload = async (relatorio) => {
        setDownloadingId(relatorio.id)
        await downloadRelatorioObra(relatorio.id, investidor)
        setDownloadingId(null)
    }

    const handleSolicitar = async () => {
        if (!dataExibicaoId) return
        const periodo = datasExibicao.find((d) => d.id === dataExibicaoId)
        setSolicitando(true)
        const ok = await postRelatorio({ data_exibicao_id: dataExibicaoId, obra_id: obraId })
        setSolicitando(false)
        if (ok) {
            setDataExibicaoId('')
            if (periodo) {
                setRelatorios((prev) => [{
                    id: `processando-${Date.now()}`,
                    status: 'Processando',
                    data_inicial: periodo.data_inicial,
                    data_final: periodo.data_final,
                    statusDowload: false,
                    descricao: 'Solicitação recebida, em processamento',
                    data_criacao: new Date().toISOString(),
                    _local: true,
                }, ...prev.filter((r) => !r._local)])
            }
            await loadRelatorios()
        }
    }

    return (
        <div className="space-y-6">
            {investidor && (
                <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2">
                    Relatórios processados e liberados para a sua obra aparecem aqui para download.
                </p>
            )}

            {!investidor && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                        <div>
                            <h2 className="text-base font-bold text-gray-900">Solicitar relatório</h2>
                            <p className="text-sm text-gray-400 mt-0.5">
                                Gere um novo relatório para <span className="font-medium text-gray-600">{obraNome || 'esta obra'}</span>
                            </p>
                        </div>
                        <div className="p-2 rounded-xl bg-gray-900">
                            <PlusIcon className="w-4 h-4 text-brand-yellow" />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                <CalendarDaysIcon className="w-3.5 h-3.5" /> Data de exibição
                            </label>
                            {loadingDatas ? (
                                <div className="flex items-center gap-2 text-sm text-gray-400 py-2.5">
                                    <span className="h-4 w-4 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin" />
                                    Carregando datas...
                                </div>
                            ) : (
                                <select
                                    value={dataExibicaoId}
                                    onChange={(e) => setDataExibicaoId(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-yellow/40 focus:border-brand-yellow bg-white"
                                >
                                    <option value="">Selecione o período</option>
                                    {datasExibicao.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.data_inicial} — {item.data_final}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="flex items-end">
                            <button
                                type="button"
                                onClick={handleSolicitar}
                                disabled={!dataExibicaoId || solicitando}
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {solicitando ? (
                                    <span className="h-4 w-4 rounded-full border-2 border-gray-900/20 border-t-gray-900 animate-spin" />
                                ) : (
                                    <DocumentChartBarIcon className="w-4 h-4" />
                                )}
                                {solicitando ? 'Solicitando...' : 'Solicitar relatório'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <div className="p-1.5 bg-gray-900 rounded-lg">
                        <DocumentChartBarIcon className="w-4 h-4 text-brand-yellow" />
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900">
                        {investidor ? 'Relatórios disponíveis para download' : 'Relatórios desta obra'}
                    </h2>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <span className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin mb-3" />
                        <p className="text-sm text-gray-400">Verificando relatórios...</p>
                    </div>
                ) : relatorios.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                        <DocumentChartBarIcon className="w-10 h-10 text-gray-300 mb-3" />
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">
                            {investidor ? 'Nenhum relatório liberado ainda' : 'Nenhum relatório concluído encontrado'}
                        </h3>
                        <p className="text-xs text-gray-400">
                            {investidor
                                ? 'Quando um relatório for processado e liberado para você, ele aparecerá aqui para download.'
                                : 'Relatórios em processamento podem ser acompanhados na página de Relatórios até ficarem prontos para download.'}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {relatorios.map((item) => {
                            const statusLabel = normalizeRelatorioStatus(item.status)
                            return (
                                <div key={item.id} className="px-5 py-4 hover:bg-gray-50/80 transition-colors">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div>
                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Período</p>
                                                <p className="text-sm font-medium text-gray-900">{item.data_inicial} — {item.data_final}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Status</p>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${relatorioStatusStyle(item.status)}`}>
                                                    {statusLabel}
                                                </span>
                                                {item.etapa && (
                                                    <p className="text-xs text-gray-400 mt-1">{item.etapa}</p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Criado em</p>
                                                <p className="text-sm text-gray-600 flex items-center gap-1">
                                                    <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                                                    {formatDateTime(item.data_criacao)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex-shrink-0">
                                            {item.statusDowload ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleDownload(item)}
                                                    disabled={downloadingId === item.id || item._local}
                                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark transition-colors disabled:opacity-50"
                                                >
                                                    {downloadingId === item.id ? (
                                                        <span className="h-4 w-4 rounded-full border-2 border-gray-900/20 border-t-gray-900 animate-spin" />
                                                    ) : (
                                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                                    )}
                                                    Download
                                                </button>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">
                                                    {item._local ? 'Aguardando processamento...' : 'Download indisponível'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {item.descricao && (
                                        <p className="text-xs text-gray-500 mt-2 truncate" title={item.descricao}>{item.descricao}</p>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

export default RelatorioObraTab
